/* eslint-disable tailwindcss/no-custom-classname -- SVG animation class names are defined in the inline <style>, not Tailwind utilities. */
import { cn } from '@heidi/utils';
import * as React from 'react';

interface LogoAnimationProps {
  className?: string;
  'data-testid'?: string;
}

/**
 * Private implementation detail of HeidiLoader — the animated Heidi brand mark.
 * Not a public door; consumers use `<HeidiLoader>` (with `decorative` for the
 * non-loading, flourish use case).
 */
const LogoAnimation = React.forwardRef<SVGSVGElement, LogoAnimationProps>(
  ({ className, 'data-testid': testId }, ref) => {
    return (
      <svg
        ref={ref}
        aria-hidden="true"
        data-testid={testId}
        // Fixed brand colour, flipped to a light silhouette in dark mode for
        // contrast. Inlined rather than the app's `dark-invert` utility so the
        // animation stays self-contained (package / Storybook / docs site).
        className={cn('block newBrandDark:brightness-0 newBrandDark:invert', className)}
        focusable="false"
        viewBox="0 0 1080 1080"
        xmlns="http://www.w3.org/2000/svg"
      >
        <style>
          {`
          .heidi-logo-motion {
            transform-box: view-box;
            transform-origin: 0 0;
          }

          .heidi-logo-scale {
            transform-box: fill-box;
            transform-origin: center;
          }

          .heidi-logo-motion--top-left {
            animation: heidi-logo-position-top-left 3.333s infinite;
          }

          .heidi-logo-motion--top-right {
            animation: heidi-logo-position-top-right 3.333s infinite;
          }

          .heidi-logo-motion--bottom-left {
            animation: heidi-logo-position-bottom-left 3.333s infinite;
          }

          .heidi-logo-motion--bottom-right {
            animation: heidi-logo-position-bottom-right 3.333s infinite;
          }

          .heidi-logo-scale--top-left {
            animation: heidi-logo-scale 3.333s infinite;
            --heidi-logo-scale-x: 5.614345;
            --heidi-logo-scale-y: 5.614345;
          }

          .heidi-logo-scale--top-right {
            animation: heidi-logo-scale 3.333s infinite;
            --heidi-logo-scale-x: -5.614345;
            --heidi-logo-scale-y: 5.614345;
          }

          .heidi-logo-scale--bottom-left {
            animation: heidi-logo-scale 3.333s infinite;
            --heidi-logo-scale-x: 5.614345;
            --heidi-logo-scale-y: -5.614345;
          }

          .heidi-logo-scale--bottom-right {
            animation: heidi-logo-scale 3.333s infinite;
            --heidi-logo-scale-x: -5.614345;
            --heidi-logo-scale-y: -5.614345;
          }

          @keyframes heidi-logo-scale {
            0% {
              transform: scale(0);
              animation-timing-function: cubic-bezier(1, 0, 0.317, 1);
            }

            22.1875%,
            80% {
              transform: scale(var(--heidi-logo-scale-x), var(--heidi-logo-scale-y));
              animation-timing-function: cubic-bezier(0.167, 0, 0.257, 1);
            }

            92.5%,
            100% {
              transform: scale(0);
            }
          }

          @keyframes heidi-logo-position-top-left {
            0% {
              transform: translate(640px, 540px);
              animation-timing-function: cubic-bezier(0.821, 0, 0.268, 1);
            }

            37.90375%,
            58.2425% {
              transform: translate(540px, 540px);
              animation-timing-function: cubic-bezier(0.569, 0, 0.187, 1);
            }

            88.75%,
            100% {
              transform: translate(640px, 598.4875px);
            }
          }

          @keyframes heidi-logo-position-top-right {
            0% {
              transform: translate(440px, 540px);
              animation-timing-function: cubic-bezier(0.821, 0, 0.268, 1);
            }

            37.90375%,
            58.2425% {
              transform: translate(540px, 540px);
              animation-timing-function: cubic-bezier(0.569, 0, 0.187, 1);
            }

            88.75%,
            100% {
              transform: translate(440px, 598.4875px);
            }
          }

          @keyframes heidi-logo-position-bottom-left {
            0% {
              transform: translate(640px, 540px);
              animation-timing-function: cubic-bezier(0.821, 0, 0.268, 1);
            }

            37.90375%,
            58.2425% {
              transform: translate(540px, 540px);
              animation-timing-function: cubic-bezier(0.569, 0, 0.187, 1);
            }

            88.75%,
            100% {
              transform: translate(640px, 481.5125px);
            }
          }

          @keyframes heidi-logo-position-bottom-right {
            0% {
              transform: translate(440px, 540px);
              animation-timing-function: cubic-bezier(0.821, 0, 0.268, 1);
            }

            37.90375%,
            58.2425% {
              transform: translate(540px, 540px);
              animation-timing-function: cubic-bezier(0.569, 0, 0.187, 1);
            }

            88.75%,
            100% {
              transform: translate(440px, 481.5125px);
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .heidi-logo-motion,
            .heidi-logo-scale {
              animation: none;
            }

            .heidi-logo-motion {
              transform: translate(540px, 540px);
            }

            .heidi-logo-scale {
              transform: scale(var(--heidi-logo-scale-x), var(--heidi-logo-scale-y));
            }
          }
        `}
        </style>

        <HeidiLogoQuadrant position="top-left" viewBox="0 0 540 540" x={0} y={0} />
        <HeidiLogoQuadrant position="top-right" viewBox="540 0 540 540" x={540} y={0} />
        <HeidiLogoQuadrant position="bottom-left" viewBox="0 540 540 540" x={0} y={540} />
        <HeidiLogoQuadrant
          position="bottom-right"
          viewBox="540 540 540 540"
          x={540}
          y={540}
        />
      </svg>
    );
  }
);
LogoAnimation.displayName = 'LogoAnimation';

const HeidiLogoQuadrant = ({
  position,
  viewBox,
  x,
  y,
}: {
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  viewBox: string;
  x: number;
  y: number;
}) => (
  <svg height="540" overflow="hidden" viewBox={viewBox} width="540" x={x} y={y}>
    <g className={`heidi-logo-motion heidi-logo-motion--${position}`}>
      <g className={`heidi-logo-scale heidi-logo-scale--${position}`}>
        <HeidiLogoMark />
      </g>
    </g>
  </svg>
);

const HeidiLogoMark = () => (
  // theme-agnostic: the solid Heidi brand mark (bark/900). Dark mode flips it to
  // a light silhouette via the `newBrandDark:` classes on the parent <svg>.
  <g fill="#28030F" fillRule="nonzero" transform="translate(-96.949 -96.995)">
    <path
      d="M 44.087 11.184 C 40.162 13.537, 35.37 15.853, 25.585 17.288 C 23.577 17.579, 21.806 15.981, 21.907 13.973 C 22.6 0.664, 24.727 -5.541, 27.967 -10.874 C 31.098 -16.035, 35.799 -18.77, 41.559 -18.77 C 47.318 -18.77, 54.146 -12.992, 54.146 -6.251 C 54.146 -1.99, 52.84 5.941, 44.096 11.174 C 44.096 11.174, 44.087 11.184, 44.087 11.184 Z M -44.123 11.184 C -52.868 5.951, -54.173 -1.981, -54.173 -6.242 C -54.173 -12.983, -47.254 -18.76, -41.586 -18.76 C -35.918 -18.76, -31.125 -16.035, -27.995 -10.865 C -24.764 -5.532, -22.628 0.664, -21.934 13.982 C -21.833 15.999, -23.605 17.598, -25.612 17.298 C -35.406 15.854, -40.189 13.537, -44.114 11.193 C -44.114 11.193, -44.123 11.184, -44.123 11.184 Z M 41.549 -30.525 C 31.719 -30.525, 23.075 -25.574, 17.845 -16.952 C 13.719 -10.157, 11.008 -2.617, 10.141 12.736 C 9.949 16.071, 7.211 18.697, 3.861 18.742 C 2.61 18.761, 1.323 18.77, -0.009 18.77 C -1.342 18.77, -2.629 18.77, -3.88 18.742 C -7.229 18.697, -9.976 16.071, -10.159 12.736 C -11.027 -2.617, -13.737 -10.157, -17.863 -16.952 C -23.102 -25.574, -31.737 -30.525, -41.568 -30.525 C -53.087 -30.525, -65.975 -20.142, -65.975 -6.242 C -65.975 5.241, -60.225 15.272, -50.193 21.268 C -40.637 26.983, -28.907 30.525, 0 30.525 C 28.907 30.525, 40.637 26.983, 50.193 21.268 C 60.225 15.272, 65.975 5.241, 65.975 -6.242 C 65.975 -20.142, 53.086 -30.525, 41.568 -30.525 C 41.568 -30.525, 41.549 -30.525, 41.549 -30.525 Z"
      transform="translate(96.954 60.292)"
    />
    <path
      d="M 70.037 2.661 C 82.058 13.881, 88.238 31.233, 83.51 49.712 C 79.32 66.056, 66.377 78.931, 49.965 83.1 C 31.39 87.807, 13.957 81.656, 2.684 69.673 C 1.232 68.129, -1.214 68.129, -2.665 69.673 C -13.938 81.647, -31.372 87.807, -49.947 83.1 C -66.367 78.939, -79.311 66.056, -83.491 49.712 C -88.228 31.233, -82.04 13.881, -70.019 2.661 C -68.467 1.216, -68.467 -1.217, -70.019 -2.661 C -82.04 -13.881, -88.219 -31.235, -83.491 -49.714 C -79.301 -66.057, -66.358 -78.931, -49.947 -83.101 C -31.372 -87.807, -13.938 -81.656, -2.665 -69.673 C -1.214 -68.129, 1.232 -68.129, 2.684 -69.673 C 13.957 -81.647, 31.39 -87.807, 49.965 -83.101 C 66.386 -78.94, 79.329 -66.057, 83.51 -49.714 C 88.247 -31.235, 82.058 -13.881, 70.037 -2.661 C 68.485 -1.217, 68.485 1.216, 70.037 2.661 Z M 96.069 -41.755 C 94.773 -70.918, 71.078 -94.62, 41.787 -96.047 C 27.447 -96.746, 14.193 -92.177, 3.806 -84.127 C 1.588 -82.401, -1.56 -82.401, -3.788 -84.127 C -14.175 -92.186, -27.429 -96.746, -41.768 -96.047 C -71.059 -94.62, -94.755 -70.918, -96.06 -41.755 C -96.699 -27.401, -91.989 -14.164, -83.756 -3.825 C -81.976 -1.59, -81.976 1.598, -83.756 3.824 C -91.989 14.163, -96.699 27.401, -96.06 41.755 C -94.764 70.917, -71.068 94.62, -41.777 96.046 C -27.438 96.746, -14.184 92.175, -3.797 84.126 C -1.579 82.4, 1.57 82.4, 3.797 84.126 C 14.185 92.175, 27.438 96.746, 41.778 96.046 C 71.068 94.62, 94.764 70.917, 96.06 41.755 C 96.699 27.401, 91.989 14.163, 83.756 3.824 C 81.976 1.589, 81.976 -1.6, 83.756 -3.825 C 91.989 -14.155, 96.699 -27.401, 96.06 -41.755 C 96.06 -41.755, 96.069 -41.755, 96.069 -41.755 Z"
      transform="translate(96.949 96.996)"
    />
    <path
      d="M -44.078 -11.193 C -40.153 -13.546, -35.361 -15.862, -25.576 -17.298 C -23.568 -17.589, -21.797 -15.99, -21.898 -13.982 C -22.591 -0.673, -24.718 5.532, -27.959 10.865 C -31.089 16.026, -35.79 18.76, -41.55 18.76 C -47.309 18.76, -54.137 12.982, -54.137 6.241 C -54.137 1.98, -52.831 -5.951, -44.087 -11.184 C -44.087 -11.184, -44.078 -11.193, -44.078 -11.193 Z M 44.132 -11.193 C 52.877 -5.96, 54.182 1.971, 54.182 6.232 C 54.182 12.973, 47.263 18.751, 41.595 18.751 C 35.926 18.751, 31.134 16.026, 28.004 10.856 C 24.772 5.524, 22.637 -0.673, 21.943 -13.991 C 21.843 -16.008, 23.613 -17.607, 25.621 -17.307 C 35.415 -15.862, 40.198 -13.546, 44.123 -11.202 C 44.123 -11.202, 44.132 -11.193, 44.132 -11.193 Z M -41.55 30.525 C -31.719 30.525, -23.075 25.574, -17.845 16.952 C -13.719 10.156, -11.008 2.616, -10.141 -12.737 C -9.95 -16.072, -7.211 -18.697, -3.861 -18.743 C -2.611 -18.761, -1.324 -18.77, 0.009 -18.77 C 1.342 -18.77, 2.628 -18.77, 3.879 -18.743 C 7.229 -18.697, 9.976 -16.072, 10.159 -12.737 C 11.026 2.616, 13.737 10.156, 17.863 16.952 C 23.102 25.574, 31.737 30.525, 41.567 30.525 C 53.087 30.525, 65.975 20.141, 65.975 6.241 C 65.975 -5.242, 60.224 -15.272, 50.193 -21.268 C 40.636 -26.983, 28.907 -30.525, -0.001 -30.525 C -28.908 -30.525, -40.637 -26.983, -50.194 -21.268 C -60.225 -15.272, -65.975 -5.242, -65.975 6.241 C -65.975 20.141, -53.087 30.525, -41.568 30.525 C -41.568 30.525, -41.55 30.525, -41.55 30.525 Z"
      transform="translate(96.954 133.699)"
    />
  </g>
);

export { LogoAnimation };
